import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search, LogOut, Plus, Trash2, UserCheck, MapPin, Filter,
  LayoutDashboard, Users, ChevronRight, Building2, Home as HomeIcon, Briefcase,
  Calendar, Phone, Mail, Eye, ClipboardList,
  Clock, Award, FileText, type LucideIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Lead, Instalador, EstadoLead, TipoImovel,
  InstaladorComelit,
  getLeads, getInstaladores, deleteLead, addInstalador, deleteInstalador, deleteInstaladorComelit,
  getInstalaldoresComelit, updateInstaladorComelit,
  ESTADO_LABELS, ESTADO_COLORS, TIPO_IMOVEL_LABELS, DASHBOARD_PASSWORD,
  formatDate, formatDateShort, generateLeadCode,
} from '@/lib/index';
import { queueEmail } from '@/lib/airtable';

const TIPO_ICON: Record<TipoImovel, LucideIcon> = {
  predio: Building2,
  moradia: HomeIcon,
  escritorio: Briefcase,
};

type Tab = 'resumo' | 'leads' | 'instaladores';

export default function Dashboard() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [tab, setTab] = useState<Tab>('resumo');

  const [leads, setLeads] = useState<Lead[]>([]);
  const [instaladores, setInstaladores] = useState<Instalador[]>([]);
  const [instaladoresComelit, setInstalaldoresComelit] = useState<InstaladorComelit[]>([]);

  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState<EstadoLead | 'todos'>('todos');
  const [filterZona, setFilterZona] = useState('todos');
  const [sortLeads, setSortLeads] = useState<'data' | 'nome' | 'zona'>('data');

  // Dialogs
  const [showAddIns, setShowAddIns] = useState(false);
  const [newIns, setNewIns] = useState({ nome: '', zona: '', telefone: '', email: '' });

  const loadData = useCallback(async () => {
    const [l, i, ic] = await Promise.all([
      getLeads(), getInstaladores(), getInstalaldoresComelit(),
    ]);
    setLeads(l); setInstaladores(i); setInstalaldoresComelit(ic);
  }, []);

  useEffect(() => { if (authed) loadData(); }, [authed, loadData]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === DASHBOARD_PASSWORD) { setAuthed(true); setLoginError(''); }
    else setLoginError('Palavra-passe incorreta.');
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteInsId, setConfirmDeleteInsId] = useState<string | null>(null);

  const handleDeleteLead = async (id: string) => {
    await deleteLead(id);
    setConfirmDeleteId(null);
    loadData();
  };

  const handleAddInstalador = async () => {
    if (!newIns.nome || !newIns.zona) return;
    await addInstalador(newIns);
    setNewIns({ nome: '', zona: '', telefone: '', email: '' });
    setShowAddIns(false); loadData();
  };

  const handleDeleteInstalador = async (id: string) => {
    if (!confirm('Eliminar?')) return;
    await deleteInstalador(id); loadData();
  };

  const handleAprovarComelit = async (id: string, status: 'aprovado' | 'rejeitado') => {
    await updateInstaladorComelit(id, { status });
    const comelit = instaladoresComelit.find((i) => i.id === id);
    if (status === 'aprovado' && comelit) {
      const jaExiste = instaladores.find((i) => i.email === comelit.email);
      if (!jaExiste) {
        await addInstalador({ nome: comelit.nome, zona: comelit.zona, telefone: comelit.telefone, email: comelit.email });
      }
      // Email de aprovação ao instalador — via Airtable EmailQueue
      await queueEmail({
        email: comelit.email,
        nome:  comelit.nome,
        assunto: 'AT CONTERA — A sua conta foi aprovada! ✅',
        mensagem: [
          `Olá ${comelit.nome},`,
          ``,
          `A sua conta de Instalador Certificado Comelit AT CONTERA foi APROVADA!`,
          ``,
          `Já pode aceder ao portal e começar a receber pedidos de orçamento na zona ${comelit.zona}.`,
          ``,
          `🔗 Aceder ao portal: ${window.location.origin}/#/instalador`,
          ``,
          `━━━━━━━━━━━━━━━━━━━━━━━━━━`,
          `O que fazer agora:`,
          `1. Faça login com o email e palavra-passe que registou`,
          `2. Veja os pedidos de orçamento disponíveis na sua zona`,
          `3. Manifeste interesse e aguarde contacto da AT CONTERA`,
          `━━━━━━━━━━━━━━━━━━━━━━━━━━`,
          ``,
          `Bem-vindo à rede AT CONTERA!`,
          `AT CONTERA | comercial@contera.pt`,
        ].join('\n'),
      });
    }
    if (status === 'rejeitado' && comelit) {
      await queueEmail({
        email: comelit.email,
        nome:  comelit.nome,
        assunto: 'AT CONTERA — Atualização do seu registo',
        mensagem: [
          `Olá ${comelit.nome},`,
          ``,
          `Após análise do seu pedido de registo, não foi possível aprovar a sua conta neste momento.`,
          ``,
          `Para mais informações ou para submeter nova candidatura, entre em contacto connosco.`,
          ``,
          `AT CONTERA | comercial@contera.pt`,
        ].join('\n'),
      });
    }
    loadData();
  };

  const zonas = [...new Set(instaladores.map((i) => i.zona))].sort();

  const filteredLeads = leads.filter((l) => {
    const matchSearch =
      !search ||
      l.nome.toLowerCase().includes(search.toLowerCase()) ||
      l.email.toLowerCase().includes(search.toLowerCase()) ||
      l.localidade.toLowerCase().includes(search.toLowerCase()) ||
      generateLeadCode(l.id).toLowerCase().includes(search.toLowerCase());
    const matchEstado = filterEstado === 'todos' || l.estado === filterEstado;
    const matchZona = filterZona === 'todos' || instaladores.find((i) => i.id === l.instaladorId)?.zona === filterZona;
    return matchSearch && matchEstado && matchZona;
  }).sort((a, b) => {
    if (sortLeads === 'nome') return a.nome.localeCompare(b.nome, 'pt');
    if (sortLeads === 'zona') return (a.localidade ?? '').localeCompare(b.localidade ?? '', 'pt');
    return b.criadoEm?.localeCompare(a.criadoEm ?? '') ?? 0;
  });

  const statCounts = {
    total: leads.length,
    novos: leads.filter((l) => l.estado === 'novo').length,
    emCurso: leads.filter((l) => l.estado === 'obra_em_curso').length,
    concluidos: leads.filter((l) => l.estado === 'concluido').length,
  };

  const pendentesCount = instaladoresComelit.filter((i) => i.status === 'pendente').length;

  // ── Nav items ─────────────────────────────────────────────────────────────────
  const navItems: { id: Tab; icon: LucideIcon; label: string; count?: number; alert?: boolean }[] = [
    { id: 'resumo', icon: LayoutDashboard, label: 'Resumo', },
    { id: 'leads', icon: ClipboardList, label: 'Pedidos', count: leads.length },
    { id: 'instaladores', icon: Users, label: 'Instaladores', count: instaladoresComelit.length, alert: pendentesCount > 0 },
  ];

  // ── Login ─────────────────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ type: 'spring', stiffness: 300, damping: 35 }} className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-4">
              <span className="text-primary-foreground font-bold text-2xl">C</span>
            </div>
            <h1 className="text-2xl font-bold text-foreground">AT CONTERA</h1>
            <p className="text-muted-foreground text-sm mt-1">Dashboard Interno</p>
          </div>
          <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="pwd">Palavra-passe</Label>
                <Input id="pwd" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
                {loginError && <p className="text-destructive text-xs">{loginError}</p>}
              </div>
              <Button type="submit" className="w-full">Entrar</Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // ── Dashboard ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-56 shrink-0 border-r border-border bg-card flex flex-col py-6 px-3 hidden lg:flex">
        <div className="flex items-center gap-2 px-3 mb-8">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">C</span>
          </div>
          <div>
            <p className="font-bold text-sm text-foreground">AT CONTERA</p>
            <p className="text-xs text-muted-foreground">Dashboard</p>
          </div>
        </div>
        <nav className="space-y-1 flex-1">
          {navItems.map((item) => (
            <button key={item.id} onClick={() => setTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === item.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}>
              <item.icon size={16} />
              {item.label}
              <span className={`ml-auto text-xs px-1.5 py-0.5 rounded-full ${tab === item.id ? 'bg-primary-foreground/20 text-primary-foreground' : item.alert ? 'bg-red-100 text-red-600' : 'bg-muted text-muted-foreground'}`}>
                {item.alert ? pendentesCount : item.count}
              </span>
            </button>
          ))}
        </nav>
        <button onClick={() => setAuthed(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-destructive rounded-lg hover:bg-destructive/8 transition-colors mt-auto">
          <LogOut size={15} />Sair
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center justify-between px-3 py-3 border-b border-border bg-card gap-2 overflow-x-auto">
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xs">C</span>
            </div>
          </div>
          <div className="flex gap-1 overflow-x-auto">
            {navItems.map((item) => (
              <Button key={item.id} variant={tab === item.id ? 'default' : 'ghost'} size="sm" className="text-xs shrink-0" onClick={() => setTab(item.id)}>
                {item.label}
                {item.alert && <span className="ml-1 w-2 h-2 rounded-full bg-red-500 inline-block" />}
              </Button>
            ))}
            <Button variant="ghost" size="sm" onClick={() => setAuthed(false)}><LogOut size={14} /></Button>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-auto">

          {/* ── RESUMO INICIAL (sempre visível no topo) ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Pedidos novos', value: statCounts.novos, color: 'text-blue-600', bg: 'bg-blue-50', onClick: () => setTab('leads') },
              { label: 'Obras em curso', value: statCounts.emCurso, color: 'text-orange-600', bg: 'bg-orange-50', onClick: () => setTab('leads') },
              { label: 'Instaladores pendentes', value: instaladoresComelit.filter(i=>i.status==='pendente').length, color: 'text-yellow-600', bg: 'bg-yellow-50', onClick: () => setTab('instaladores') },
              { label: 'Obras concluídas', value: statCounts.concluidos, color: 'text-green-600', bg: 'bg-green-50', onClick: () => setTab('leads') },
            ].map((s) => (
              <button key={s.label} onClick={s.onClick} className={`${s.bg} border border-border rounded-xl p-4 text-left hover:opacity-80 transition-opacity cursor-pointer`}>
                <p className={`text-2xl font-extrabold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{s.label}</p>
              </button>
            ))}
          </div>

          {/* ── LEADS ── */}
          {tab === 'leads' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                {[
                  { label: 'Total', value: statCounts.total, color: 'text-foreground' },
                  { label: 'Novos', value: statCounts.novos, color: 'text-blue-600' },
                  { label: 'Em Curso', value: statCounts.emCurso, color: 'text-orange-600' },
                  { label: 'Concluídos', value: statCounts.concluidos, color: 'text-green-600' },
                ].map((s) => (
                  <div key={s.label} className="bg-card border border-border rounded-xl p-4">
                    <p className="text-xs text-muted-foreground">{s.label}</p>
                    <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 mb-5">
                <div className="relative flex-1 min-w-48">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input className="pl-9" placeholder="Nome, email, localidade, referência..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <Select value={filterEstado} onValueChange={(v) => setFilterEstado(v as EstadoLead | 'todos')}>
                  <SelectTrigger className="w-44"><Filter size={13} className="mr-1.5 text-muted-foreground" /><SelectValue placeholder="Estado" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os estados</SelectItem>
                    {(Object.entries(ESTADO_LABELS) as [EstadoLead, string][]).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filterZona} onValueChange={setFilterZona}>
                  <SelectTrigger className="w-40"><MapPin size={13} className="mr-1.5 text-muted-foreground" /><SelectValue placeholder="Zona" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todas as zonas</SelectItem>
                    {zonas.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={sortLeads} onValueChange={(v) => setSortLeads(v as 'data' | 'nome' | 'zona')}>
                  <SelectTrigger className="w-40"><Filter size={13} className="mr-1.5 text-muted-foreground" /><SelectValue placeholder="Ordenar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="data">Mais recentes</SelectItem>
                    <SelectItem value="nome">Nome A→Z</SelectItem>
                    <SelectItem value="zona">Zona A→Z</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {filteredLeads.length === 0 ? (
                <div className="text-center py-20 text-muted-foreground">
                  <LayoutDashboard size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Sem pedidos encontrados</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredLeads.map((lead) => {
                    const TipoIcon = TIPO_ICON[lead.tipoImovel];
                    const insAtribuidos = instaladores.filter((i) =>
                      lead.instaladorIds?.includes(i.id) || lead.instaladorId === i.id
                    );
                    return (
                      <motion.div key={lead.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-card border border-border rounded-xl p-4 flex items-center gap-4 hover:border-primary/30 transition-colors group">
                        <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <TipoIcon size={18} className="text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-foreground truncate">{lead.nome}</span>
                            <span className="font-mono text-xs text-muted-foreground">{generateLeadCode(lead.id)}</span>
                            <Badge className={`text-xs border ${ESTADO_COLORS[lead.estado]}`} variant="outline">{ESTADO_LABELS[lead.estado]}</Badge>
                            {lead.numeroFatura && <Badge variant="outline" className="text-xs border-green-200 bg-green-50 text-green-700">Fatura: {lead.numeroFatura}</Badge>}

                          </div>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={11} />{lead.localidade} · {TIPO_IMOVEL_LABELS[lead.tipoImovel]}</span>
                            <span className="text-xs text-muted-foreground flex items-center gap-1"><Calendar size={11} />Visita: {formatDateShort(lead.dataVisita)}</span>
                            {insAtribuidos.length > 0 && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <UserCheck size={11} />
                                {insAtribuidos.map((i) => i.nome).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => navigate(`/dashboard/lead/${lead.id}`)}>
                            <Eye size={15} />
                          </Button>
                          {confirmDeleteId === lead.id ? (
                            <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-lg px-2 py-1">
                              <span className="text-xs text-red-700 font-medium">Eliminar?</span>
                              <Button size="sm" className="h-6 px-2 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDeleteLead(lead.id)}>Sim</Button>
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setConfirmDeleteId(null)}>Não</Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:bg-destructive/10" onClick={() => setConfirmDeleteId(lead.id)}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                          <ChevronRight size={16} className="text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => navigate(`/dashboard/lead/${lead.id}`)} />
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}

          {/* ── INSTALADORES INTERNOS ── */}
          {tab === 'instaladores' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-foreground">Instaladores</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">Instaladores registados pelo portal e adicionados manualmente.</p>
                </div>
                <Button onClick={() => setShowAddIns(true)} size="sm"><Plus size={15} className="mr-1.5" />Adicionar manualmente</Button>
              </div>
              {pendentesCount > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-4 flex items-center gap-2">
                  <Clock size={16} className="text-yellow-600 shrink-0" />
                  <p className="text-sm text-yellow-700 font-medium">{pendentesCount} pedido{pendentesCount > 1 ? 's' : ''} de registo em análise — aprovação pendente</p>
                </div>
              )}
              {instaladoresComelit.length === 0 ? (
                <div className="text-center py-20 text-muted-foreground"><Users size={40} className="mx-auto mb-3 opacity-30" /><p>Nenhum instalador ainda.</p><p className="text-sm mt-1">Adicione manualmente ou aguarde registos pelo portal.</p></div>
              ) : (
                <div className="space-y-3">
                  {instaladoresComelit.map((ins) => (
                    <div key={ins.id} className="bg-card border border-border rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="font-semibold text-sm text-foreground">{ins.nome}</p>
                            <Badge className={`text-xs border ${ins.status === 'aprovado' ? 'bg-green-100 text-green-700 border-green-200' : ins.status === 'pendente' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' : 'bg-red-100 text-red-700 border-red-200'}`}>
                              {ins.status === 'aprovado' ? '✓ Aprovado' : ins.status === 'pendente' ? '⏳ Pendente' : '✗ Rejeitado'}
                            </Badge>
                            {ins.temSeguro && <Badge className="text-xs bg-blue-50 text-blue-700 border-blue-200 border">Seguro ✓</Badge>}
                            {ins.proximaCertificacao && (() => {
                              const dias = Math.round((new Date(ins.proximaCertificacao).getTime() - Date.now()) / 86400000);
                              if (dias < 0) return <Badge className="text-xs bg-red-100 text-red-700 border-red-200 border">Cert. expirada</Badge>;
                              if (dias <= 30) return <Badge className="text-xs bg-orange-100 text-orange-700 border-orange-200 border">Cert. expira em {dias}d</Badge>;
                              return <Badge className="text-xs bg-green-50 text-green-700 border-green-200 border">Cert. válida</Badge>;
                            })()}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><MapPin size={11} />{ins.zona}</span>
                            <span className="flex items-center gap-1"><Phone size={11} />{ins.telefone}</span>
                            <span className="flex items-center gap-1"><Mail size={11} />{ins.email}</span>
                            <span>NIF: {ins.nif}</span>
                            {ins.numeroClienteContera && <span>Nº AT CONTERA: {ins.numeroClienteContera}</span>}
                            <span>Registado: {formatDate(ins.criadoEm)}</span>
                          </div>
                          {/* Próxima Certificação */}
                          <div className="mt-3 pt-3 border-t border-border flex items-center gap-3">
                            <Award size={13} className="text-muted-foreground shrink-0" />
                            <span className="text-xs text-muted-foreground shrink-0">Próxima certificação Comelit:</span>
                            <input
                              type="date"
                              className="text-xs border border-border rounded-md px-2 py-1 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                              value={ins.proximaCertificacao ?? ''}
                              onChange={async (e) => {
                                await updateInstaladorComelit(ins.id, { proximaCertificacao: e.target.value || undefined });
                                loadData();
                              }}
                            />
                            {ins.proximaCertificacao && (
                              <span className="text-xs text-muted-foreground">{formatDate(ins.proximaCertificacao)}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3 pt-3 border-t border-border flex-wrap">
                          {ins.status === 'pendente' && (
                            <button
                              style={{background:'#16a34a',color:'#fff',border:'none',borderRadius:'6px',padding:'6px 14px',fontSize:'12px',fontWeight:600,cursor:'pointer'}}
                              onClick={() => handleAprovarComelit(ins.id, 'aprovado')}
                            >✓ Aprovar</button>
                          )}
                          {ins.status === 'pendente' && (
                            <button
                              style={{background:'#fff',color:'#dc2626',border:'1px solid #dc2626',borderRadius:'6px',padding:'6px 14px',fontSize:'12px',fontWeight:600,cursor:'pointer'}}
                              onClick={() => handleAprovarComelit(ins.id, 'rejeitado')}
                            >✗ Rejeitar</button>
                          )}
                          {ins.status === 'aprovado' && (
                            <button
                              style={{background:'#f3f4f6',color:'#374151',border:'1px solid #d1d5db',borderRadius:'6px',padding:'6px 14px',fontSize:'12px',fontWeight:600,cursor:'pointer'}}
                              onClick={() => handleAprovarComelit(ins.id, 'rejeitado')}
                            >Suspender</button>
                          )}
                          {ins.status === 'rejeitado' && (
                            <button
                              style={{background:'#f3f4f6',color:'#374151',border:'1px solid #d1d5db',borderRadius:'6px',padding:'6px 14px',fontSize:'12px',fontWeight:600,cursor:'pointer'}}
                              onClick={() => handleAprovarComelit(ins.id, 'aprovado')}
                            >Reativar</button>
                          )}
                          <button
                            style={{background:'#fee2e2',color:'#dc2626',border:'1px solid #fca5a5',borderRadius:'6px',padding:'6px 14px',fontSize:'12px',fontWeight:600,cursor:'pointer'}}
                            onClick={async () => {
                              setConfirmDeleteInsId(ins.id);
                            }}
                          >🗑 Eliminar</button>
                          {confirmDeleteInsId === ins.id && (
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-xs text-red-600 font-medium">Confirmar?</span>
                              <button className="text-xs bg-red-600 text-white px-2 py-0.5 rounded" onClick={async () => { try { await deleteInstaladorComelit(ins.id); setConfirmDeleteInsId(null); loadData(); } catch(e) { alert('Erro: ' + e); } }}>Sim</button>
                              <button className="text-xs border px-2 py-0.5 rounded" onClick={() => setConfirmDeleteInsId(null)}>Não</button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}


          {/* ── RESUMO ── */}
          {tab === 'resumo' && (() => {
            // Cruzar dados: para cada lead, encontrar instalador e estado
            const rows = leads.map(lead => {
              const instalador = instaladoresComelit.find(i => i.id === lead.instaladorId);
              return { lead, instalador };
            });

            const estadoBadge = (estado: string) => {
              const map: Record<string, { label: string; cls: string }> = {
                novo:               { label: 'Novo',            cls: 'bg-blue-100 text-blue-700 border-blue-200' },
                em_contacto:        { label: 'Em contacto',     cls: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
                orcamentos_enviados:{ label: 'Orç. enviados',   cls: 'bg-purple-100 text-purple-700 border-purple-200' },
                visita_agendada:    { label: 'Visita agendada', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
                obra_em_curso:      { label: 'Em curso',        cls: 'bg-orange-100 text-orange-700 border-orange-200' },
                concluido:          { label: 'Concluído',       cls: 'bg-green-100 text-green-700 border-green-200' },
                cancelado:          { label: 'Cancelado',       cls: 'bg-red-100 text-red-700 border-red-200' },
              };
              const s = map[estado] || { label: estado, cls: 'bg-muted text-muted-foreground border-border' };
              return <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>;
            };

            return (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                {/* KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Total de pedidos', value: leads.length, color: 'text-foreground', bg: 'bg-card' },
                    { label: 'Obras em curso', value: leads.filter(l => l.estado === 'obra_em_curso').length, color: 'text-orange-600', bg: 'bg-orange-50' },
                    { label: 'Obras concluídas', value: leads.filter(l => l.estado === 'concluido').length, color: 'text-green-600', bg: 'bg-green-50' },
                    { label: 'Sem instalador', value: leads.filter(l => !l.instaladorId && l.estado !== 'cancelado').length, color: 'text-red-500', bg: 'bg-red-50' },
                  ].map(k => (
                    <div key={k.label} className={`${k.bg} border border-border rounded-xl p-4`}>
                      <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{k.label}</p>
                    </div>
                  ))}
                </div>

                {/* Tabela */}
                <div className="bg-card border border-border rounded-2xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                    <div>
                      <h2 className="text-base font-bold text-foreground">Quadro de Obras</h2>
                      <p className="text-xs text-muted-foreground mt-0.5">Visão completa de todos os pedidos — cliente, instalador atribuído, estado e rejeições</p>
                    </div>
                  </div>

                  {rows.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <LayoutDashboard size={32} className="mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Sem pedidos ainda.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border bg-muted/40">
                            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Código</th>
                            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Cliente</th>
                            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Zona</th>
                            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Estado</th>
                            <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Instalador atribuído</th>
                            <th className="px-4 py-3"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {rows.map(({ lead, instalador }) => (
                            <tr key={lead.id} className="hover:bg-muted/30 transition-colors group">
                              <td className="px-4 py-3">
                                <span className="font-mono text-xs text-muted-foreground">{generateLeadCode(lead.id)}</span>
                              </td>
                              <td className="px-4 py-3">
                                <p className="font-medium text-foreground text-xs">{lead.nome || '—'}</p>
                                <p className="text-xs text-muted-foreground">{lead.email}</p>
                              </td>
                              <td className="px-4 py-3 text-xs text-muted-foreground">{lead.localidade || '—'}</td>
                              <td className="px-4 py-3">{estadoBadge(lead.estado)}</td>
                              <td className="px-4 py-3">
                                {instalador ? (
                                  <div>
                                    <p className="text-xs font-semibold text-foreground">{instalador.nome}</p>
                                    <p className="text-xs text-muted-foreground">{instalador.email}</p>
                                  </div>
                                ) : (
                                  <span className="text-xs text-red-500 font-medium">Sem instalador</span>
                                )}
                              </td>

                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2 justify-end">
                                  <button onClick={() => navigate(`/dashboard/lead/${lead.id}`)} className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-primary hover:underline font-medium">Ver →</button>
                                  {confirmDeleteId === lead.id ? (
                                    <div className="flex items-center gap-1 bg-red-50 border border-red-200 rounded-lg px-2 py-1">
                                      <span className="text-xs text-red-700 font-medium">Eliminar?</span>
                                      <Button size="sm" className="h-5 px-1.5 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDeleteLead(lead.id)}>Sim</Button>
                                      <Button size="sm" variant="ghost" className="h-5 px-1.5 text-xs" onClick={() => setConfirmDeleteId(null)}>Não</Button>
                                    </div>
                                  ) : (
                                    <button onClick={() => setConfirmDeleteId(lead.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-red-700">
                                      <Trash2 size={13} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })()}

        </main>
      </div>

      {/* Dialog Instalador */}
      <Dialog open={showAddIns} onOpenChange={setShowAddIns}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Instalador</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nome *</Label><Input placeholder="João Silva" value={newIns.nome} onChange={(e) => setNewIns(p => ({ ...p, nome: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Zona *</Label><Input placeholder="Lisboa" value={newIns.zona} onChange={(e) => setNewIns(p => ({ ...p, zona: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Telefone</Label><Input placeholder="912 345 678" value={newIns.telefone} onChange={(e) => setNewIns(p => ({ ...p, telefone: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input placeholder="joao@instala.pt" value={newIns.email} onChange={(e) => setNewIns(p => ({ ...p, email: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddIns(false)}>Cancelar</Button>
            <Button onClick={handleAddInstalador} disabled={!newIns.nome || !newIns.zona}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
